import { useEffect } from "react";
import { toast } from "react-toastify";
import mergeContactPartial from "../../helpers/mergeContactPartial";

/**
 * Gestiona los 6 listeners Socket.IO del componente Ticket.
 *
 * @param {object}   socketManager — SocketContext
 * @param {string}   ticketId      — URL param UUID (re-ejecuta el effect cuando cambia)
 * @param {object}   isMounted     — ref { current: bool } — controlado por el orquestador
 * @param {object}   ticketRef     — ref { current: ticket } — evita stale closures sobre ticket state
 * @param {Function} setTicket     — setState del ticket
 * @param {Function} setContact    — setState del contact
 * @param {object}   history       — react-router history (redirect en delete)
 */
const useTicketSocket = ({
  socketManager,
  ticketId,
  isMounted,
  ticketRef,
  setTicket,
  setContact,
  history,
}) => {
  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    // ✅ Suscribirse al canal numérico del ticket activo
    const subscribeToTicket = () => {
      const currentTicketNumericId = ticketRef.current?.id;
      if (currentTicketNumericId) {
        socket.emit("joinChatBox", `${currentTicketNumericId}`);
        socket.emit("subscribe-presence", { ticketId: currentTicketNumericId });
      }
    };

    const handleReady = () => subscribeToTicket();
    const handleConnect = () => subscribeToTicket();

    socket.on("ready", handleReady);
    socket.on("connect", handleConnect);

    // ── Listener 1: ticket ────────────────────────────────────────────────────
    const handleTicketUpdate = (data) => {
      if (!isMounted.current) return;

      const eventTicketId = data.ticket?.id || data.ticketId;
      const currentTicketNumericId = ticketRef.current?.id;

      // ✅ FILTRO CRÍTICO: ignorar eventos de otros tickets
      if (currentTicketNumericId && eventTicketId && eventTicketId !== currentTicketNumericId) {
        return;
      }

      if (data.action === "update" && data.ticket) {
        if (eventTicketId && currentTicketNumericId && eventTicketId === currentTicketNumericId) {
          // ✅ Leer status desde ref para evitar stale closure
          const previousStatus = ticketRef.current?.status;

          setTicket((prevTicket) => ({
            ...prevTicket,
            ...data.ticket,
            user: data.ticket.user || prevTicket.user,
            contact: data.ticket.contact
              ? mergeContactPartial(prevTicket.contact, data.ticket.contact)
              : prevTicket.contact,
            contactId: data.ticket.contactId || prevTicket.contactId,
            userId: data.ticket.userId || prevTicket.userId,
          }));

          if (data.ticket.status === "closed" && previousStatus !== "closed") {
            toast.success("Ticket cerrado correctamente");
          }
        } else if (eventTicketId && !currentTicketNumericId && data.ticket?.id) {
          setTicket({ ...data.ticket });
        }
      }

      if (data.action === "delete") {
        if (eventTicketId && currentTicketNumericId && eventTicketId === currentTicketNumericId) {
          // ✅ Leer status desde ref para evitar stale closure
          if (ticketRef.current?.status === "closed") {
            return;
          }
          toast.info("Ticket eliminado");
          history.push("/tickets");
        }
      }
    };

    socket.on(`company-${companyId}-ticket`, handleTicketUpdate);

    // ── Listener 2: contact ───────────────────────────────────────────────────
    const handleContactUpdate = (data) => {
      if (!isMounted.current) return;

      if (data.action === "update") {
        setContact((prevState) => {
          const eventContactId = data.contact?.id;
          const matchesPrevState = prevState.id === eventContactId;
          // ✅ Leer contactId desde ref para evitar stale closure
          const matchesTicket = ticketRef.current?.contactId === eventContactId;

          if (matchesPrevState || matchesTicket || (!prevState.id && eventContactId)) {
            const merged = mergeContactPartial(prevState, data.contact);
            return {
              ...merged,
              updatedAt: data.contact.updatedAt || new Date().toISOString(),
            };
          }
          return prevState;
        });
      }
    };

    socket.on(`company-${companyId}-contact`, handleContactUpdate);

    // ── Listener 3: appMessage ────────────────────────────────────────────────
    const handleAppMessage = (data) => {
      if (!isMounted.current) return;

      const messageTicketId = data.message?.ticketId || data.ticket?.id;
      const currentTicketNumericId = ticketRef.current?.id;

      // FIX: comparar como número Y como string para evitar "1305" !== 1305
      const idsMatch =
        messageTicketId &&
        currentTicketNumericId &&
        (Number(messageTicketId) === Number(currentTicketNumericId) ||
          String(messageTicketId) === String(currentTicketNumericId));

      if (idsMatch && data.action === "create" && data.ticket) {
        setTicket((prevState) => ({
          ...prevState,
          lastMessage: data.message?.body || data.ticket.lastMessage,
          updatedAt: data.ticket.updatedAt || new Date(),
        }));
      }
    };

    socket.on(`company-${companyId}-appMessage`, handleAppMessage);

    // ── Listener 4: presence ──────────────────────────────────────────────────
    const handlePresence = (data) => {
      if (!isMounted.current) return;

      const currentTicketNumericId = ticketRef.current?.id;

      if (data.ticketId && currentTicketNumericId && data.ticketId === currentTicketNumericId) {
        setTicket((prevState) => ({ ...prevState, presence: data.presence }));

        // ✅ Auto-limpiar "composing" después de 10 segundos
        if (data.presence === "composing") {
          setTimeout(() => {
            if (!isMounted.current) return;
            setTicket((prevState) => {
              if (
                prevState.id === currentTicketNumericId &&
                prevState.presence === "composing"
              ) {
                return { ...prevState, presence: "available" };
              }
              return prevState;
            });
          }, 10000);
        }
      }
    };

    socket.on(`company-${companyId}-presence`, handlePresence);

    return () => {
      // ✅ SOLO REMOVER LISTENERS, NO DESCONECTAR EL SOCKET COMPARTIDO
      if (socket && typeof socket.off === "function") {
        socket.off(`company-${companyId}-ticket`, handleTicketUpdate);
        socket.off(`company-${companyId}-appMessage`, handleAppMessage);
        socket.off(`company-${companyId}-contact`, handleContactUpdate);
        socket.off(`company-${companyId}-presence`, handlePresence);
        socket.off("ready", handleReady);
        socket.off("connect", handleConnect);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]); // ✅ Solo re-ejecutar cuando cambia ticketId
};

export default useTicketSocket;
